import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faLayerGroup, faCode, faUser, faGamepad, faHouse, faLifeRing, faHeadset, faBookOpen, faCubes, faBriefcase, faCoins, faSliders, faBoxesStacked, faFileLines, faGaugeHigh, faWallet, faRightLeft, faUserLock, faBuilding, faScaleUnbalanced, faUserShield } from "@fortawesome/free-solid-svg-icons";
import { faComment } from "@fortawesome/free-regular-svg-icons";

import { cheatsNavSubItems } from '../config/cheatsCatalog.jsx';
import { COMPONENTS, PAGES } from '../config/pagePaths.generated.js';

import GameDecisionCenterIcon from '../assets/navbar/gameDashboard.svg?react';
import LiveServicesIcon from '../assets/navbar/liveServices.svg?react';

import RemoteConfigIcon from '../assets/navbar/live-ops/remoteConfig.svg?react';
import AbTestIcon from '../assets/navbar/live-ops/abTest.svg?react';
import SegmentationIcon from '../assets/navbar/players/segmentation.svg?react';
import StoreIcon from '../assets/navbar/live-ops/store.svg?react';
import MetadataIcon from '../assets/navbar/live-ops/metadata.svg?react';

import VersionsIcon from '../assets/navbar/live-ops/meta-data/versions.svg?react';
import FeaturesIcon from '../assets/navbar/live-ops/meta-data/features.svg?react';
import AttributesIcon from '../assets/navbar/live-ops/meta-data/attributes.svg?react';
import TemplatesIcon from '../assets/navbar/live-ops/meta-data/templates.svg?react';

import OrgIcon from '../assets/navbar/admin-settings/org.svg?react';
import GameSettingsIcon from '../assets/navbar/admin-settings/game.svg?react';
import RoleIcon from '../assets/navbar/admin-settings/role.svg?react';
import PageIcon from '../assets/navbar/admin-settings/page.svg?react';

import UsersIcon from '../assets/navbar/admin-settings/admin-settings/users.svg?react';

// No separate super-user nav: super users render userDashboardLinks filtered by their effective
// permissions (see DashboardLayout). Grant/deny in rbac-config.json and the nav follows.

export const userDashboardLinks = [
    // ── Top-level sections (rendered above the first "real" grouped section) ──
    // The old Home and Chat sections were removed alongside their pages — the
    // working copies live in src/old/milestone2.
    //
    // Homepage and Components are out of the nav (2026-10-26); their routes stay, so both open by URL.
    // Sign-in lands by game access instead (LANDING_PATH, config/accessControl.js).
    // {
    //     section: true,
    //     path: '/homepage',
    //     label: 'Homepage',
    //     icon: <FontAwesomeIcon icon={faHouse} />,
    // },
    // {
    //     section: true,
    //     path: '/components',
    //     label: 'Components',
    //     icon: <FontAwesomeIcon icon={faCubes} />,
    // },
    // ── Scoped views ──
    // Each carries a `scope` tag: entering one focuses the sidebar on that subtree alone (plus a
    // back row). They stay in THIS tree rather than living in separate per-scope trees because the
    // tree is a shared contract — PageHeader's breadcrumb, useNavIcon and RequireGameAccess all
    // resolve against it. See docs/nav-scope-alignment.md. Game and Portfolio are placeholders.
    {
        // Games are injected at render time from the org catalogue — see DashboardSidebar.
        section: true,
        scope: 'game',
        path: '/game',
        label: 'Games',
        icon: <FontAwesomeIcon icon={faGamepad} />,
    },
    {
        section: true,
        scope: 'portfolio',
        path: '/portfolio',
        // Hidden from a role without the Portfolio page (filterLinksByRole); its routes carry the same gate.
        pagePath: PAGES.PORTFOLIO,
        label: 'Portfolio',
        icon: <FontAwesomeIcon icon={faBriefcase} />,
    },

    // Admin scope: two groups rather than six sibling rows. `scope: 'admin'` + `bottom: true` keeps
    // them out of the main hub nav; it is entered from the account popup (HelpPopupButton).
    //
    // "Management" collects the five registry pages. They were five flat rows every one of which
    // ended in the word "Management", which said nothing and read as five unrelated destinations —
    // grouping them lets each child carry just its subject (Organizations, Games, Users, Roles,
    // Pages). The group itself is deliberately un-gated and path-less: it is a container, and
    // filterLinksByRole prunes a parent whose children all filter out, so a user holding none of the
    // five never sees it. getFirstAccessiblePath recurses into subItems, so adminLanding still
    // resolves to the first page the user can actually open.
    //
    // Each page keeps its OWN tabs — grouping changes the nav, not the pages.
    {
        section: true,
        bottom: true,
        scope: 'admin',
        label: 'Management',
        icon: <OrgIcon />,
        subItems: [
            { path: '/admin-settings/access-mgmt/org', label: 'Organizations', icon: <OrgIcon />, pagePath: PAGES.ORG },
            { path: '/admin-settings/game-mgmt/games', label: 'Games', icon: <GameSettingsIcon />, pagePath: PAGES.GAMES },
            { path: '/admin-settings/user-mgmt', label: 'Users', icon: <UsersIcon />, pagePath: PAGES.USER_MGMT },
            { path: '/admin-settings/role-access-mgmt/role', label: 'Roles', icon: <RoleIcon />, pagePath: PAGES.ROLE_MGMT },
            { path: '/admin-settings/role-access-mgmt/page', label: 'Pages', icon: <PageIcon />, pagePath: PAGES.PAGE_MGMT },
            // Super users: which built-in roles reach these Admin Settings pages (on top of rbac-config.json).
            { path: '/admin-settings/role-access-mgmt/admin-settings-access', label: 'Admin Settings Access', icon: <FontAwesomeIcon icon={faUserShield} />, superUserOnly: true },
        ],
    },
    // AI Credits — org administration (credit pools, usage, AI limits), at /ai-credits rather than
    // under /admin-settings, which is why scopeForPath resolves it by membership. Its pages are nav
    // rows like Profile's, each with its own URL:
    //   - Spend & Rate Limits: the org's AI limits, org-wide, so a row of its own;
    //   - Game Credits: the selected game's Overview / Ledger / User Limits — one page whose tab strip mirrors
    //     these three rows;
    //   - All Clients: super users' read-only oversight of every pool;
    //   - Limit Overrides: super users set any org's maximum AI limits (LNG's override).
    // Each row carries the gate its page applies (filterLinksByRole: componentPath + minLevel,
    // hideForSuperUser), so nobody gets a row they cannot open.
    {
        section: true,
        bottom: true,
        scope: 'admin',
        path: '/ai-credits',
        label: 'AI Credits',
        icon: <FontAwesomeIcon icon={faCoins} />,
        pagePath: PAGES.AI_CREDITS,
        subItems: [
            { path: '/ai-credits/org-limits', label: 'Spend & Rate Limits', icon: <FontAwesomeIcon icon={faGaugeHigh} />, pagePath: PAGES.AI_CREDITS },
            {
                path: '/ai-credits/game',
                label: 'Game Credits',
                icon: <FontAwesomeIcon icon={faGamepad} />,
                pagePath: PAGES.AI_CREDITS,
                subItems: [
                    { path: '/ai-credits/game/overview', label: 'Overview', icon: <FontAwesomeIcon icon={faWallet} />, pagePath: PAGES.AI_CREDITS, componentPath: COMPONENTS.AI_CREDITS_VIEW_USAGE },
                    { path: '/ai-credits/game/ledger', label: 'Ledger', icon: <FontAwesomeIcon icon={faRightLeft} />, pagePath: PAGES.AI_CREDITS, hideForSuperUser: true },
                    { path: '/ai-credits/game/limits', label: 'User Limits', icon: <FontAwesomeIcon icon={faUserLock} />, pagePath: PAGES.AI_CREDITS, componentPath: COMPONENTS.AI_CREDITS_SET_USER_LIMIT, minLevel: 'FULL_ACCESS', hideForSuperUser: true },
                ],
            },
            { path: '/ai-credits/clients', label: 'All Clients', icon: <FontAwesomeIcon icon={faBuilding} />, superUserOnly: true },
            { path: '/ai-credits/limit-overrides', label: 'Limit Overrides', icon: <FontAwesomeIcon icon={faScaleUnbalanced} />, superUserOnly: true },
        ],
    },

    // NOTE: The "Recents" section is injected dynamically by <DashboardSidebar />.

    // Analytics, Live Ops, Players, Data Intelligence and AI Studio were removed
    // alongside their pages. The full nav tree for those sections is preserved in
    // src/old/milestone2/routes/navConfig.jsx.

    // ─────────── BOTTOM-PINNED SECTIONS ───────────
    // Account scope: the signed-in user's own page — Profile, whose Platform Settings tab holds the
    // workspace preferences. Help is NOT in it (Support and Docs are product help, not account data)
    // and neither is AI Credits (an org credit pool, so it lives under admin); both carry their own
    // scope.
    //
    // `scope: …` + `bottom: true` keeps these out of the hub nav (they are entered from the account
    // popup) while giving each a focused sidebar, so the nav matches the page you are on instead of
    // showing the hub's unrelated sections. Both scopes are rootless — their pages share no path
    // prefix — so they resolve by membership in scopeForPath; being listed here IS how a URL maps to
    // its scope.
    // Reached from the HelpPopupButton (avatar popup) — DashboardSidebar
    // filters `bottom: true` items out of the main rendering. Listed here so
    // the page-header dropdown (navLookup) can find them as peers when the
    // user is on /profile, /ai-credits, etc.
    // Order = the avatar popup's: Developer Tools, Releases, Profile, Help.
    {
        section: true,
        bottom: true,
        label: 'Developer Tools',
        icon: <FontAwesomeIcon icon={faCode} />,
        // Super-user-only — not grantable via RBAC (matches the backend cheats gate).
        superUserOnly: true,
        scope: 'devtools',
        pagePath: PAGES.CHEATS_DASHBOARD,
        subItems: cheatsNavSubItems(),
    },
    {
        // Peer row of Help in the avatar popup (HelpPopupButton renders the actual flyout — this
        // entry is what navLookup uses to find it as a sibling for the page-header dropdown).
        // Listed BEFORE Help so the popup render order matches.
        //
        // Ungated like Help (no pagePath → no RBAC check): release notes and SDK downloads are
        // reference material every signed-in user should reach, whatever their role. Its own scope, so
        // its pages show only the Releases section, not Help's too.
        section: true,
        bottom: true,
        scope: 'releases',
        label: 'Releases',
        icon: <FontAwesomeIcon icon={faBoxesStacked} />,
        subItems: [
            { path: '/releases/apis', label: 'APIs Release', icon: <FontAwesomeIcon icon={faFileLines} /> },
            { path: '/releases/sdks', label: 'Client SDKs', icon: <FontAwesomeIcon icon={faCubes} /> },
        ],
    },
    {
        section: true,
        bottom: true,
        scope: 'account',
        label: 'Profile',
        icon: <FontAwesomeIcon icon={faUser} />,
        path: '/profile',
        // The page's sections are sidebar children rather than a tab strip: the Account scope showed
        // a single lonely "Profile" row next to a page carrying four tabs, so the nav said nothing
        // about where you were. Each has its own route (/profile/<section>); Change Password stays
        // listed for SSO accounts and explains itself there rather than vanishing from the nav.
        subItems: [
            { path: '/profile/general',  label: 'General',               icon: <FontAwesomeIcon icon={faUser} /> },
            { path: '/profile/password', label: 'Change Password',       icon: <FontAwesomeIcon icon={faKey} /> },
            { path: '/profile/access',   label: 'Organizations & Games', icon: <FontAwesomeIcon icon={faLayerGroup} /> },
            { path: '/profile/platform', label: 'Platform Settings',     icon: <FontAwesomeIcon icon={faSliders} /> },
        ],
    },
    {
        // Help is always visible (no pagePath → no RBAC check) — every user
        // should be able to reach Support and the Docs window regardless of
        // role.
        section: true,
        bottom: true,
        scope: 'help',
        label: 'Help',
        icon: <FontAwesomeIcon icon={faLifeRing} />,
        subItems: [
            { path: '/help/support', label: 'Support', icon: <FontAwesomeIcon icon={faHeadset} /> },
            // Docs opens in a new tab via the standalone DocsStandaloneLayout.
            // The `openInNewTab` flag is honored by DashboardSidebar's click
            // handlers (see goToPath in DashboardSidebar.jsx) and by PageMenu.
            { path: '/help/docs', label: 'Docs', icon: <FontAwesomeIcon icon={faBookOpen} />, openInNewTab: true },
        ],
    },
];

// gameDashboardLinks / gameInfoSectionLinks removed with the game-dashboard shell
// (its header and /game/* routes are gone). Preserved in
// src/old/milestone2/routes/navConfig.jsx.

// demoDashboardLinks / settingsDashboardLinks removed — both were unreferenced.
// The demo sidebar is built inline in shell/layouts/DashboardLayout.jsx.
